<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-icon-text-list__inner">
        <JssRichText class="c-icon-text-list__title" :field="fields?.title" tag="h2" />

        <div class="c-icon-text-list__layout">
          <div class="c-icon-text-list__media">
            <img
              v-if="imageSrc"
              class="c-icon-text-list__image"
              :src="imageSrc"
              :alt="imageAlt"
              loading="lazy"
            />
          </div>

          <div v-if="features.length" class="c-icon-text-list__features">
            <article
              v-for="(feature, index) in features"
              :key="feature.id"
              class="c-icon-text-list__feature"
              :class="[
                `c-icon-text-list__feature--${getTone(index)}`,
                { 'c-icon-text-list__feature--has-image': hasFeatureImage(feature) },
              ]"
            >
              <img
                v-if="hasFeatureImage(feature)"
                class="c-icon-text-list__feature-image"
                :src="getFeatureImageSrc(feature)"
                :alt="getFeatureImageAlt(feature)"
                loading="lazy"
              />
              <RichText
                v-if="feature.fields?.description?.value"
                class="c-icon-text-list__feature-description"
                :field="feature.fields.description"
              />
            </article>
          </div>
        </div>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import utils from '@/utils';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import type { ScItem } from '@/types/sitecore';
import type { SplitLayoutFeatureSectionFields } from '@/types/components/SplitLayoutFeatureSection';
import type { SplitLayoutFeatureSectionFeatureFields } from '@/types/components/SplitLayoutFeatureSection';

interface SplitLayoutFeatureSectionProps {
  fields: SplitLayoutFeatureSectionFields;
  rendering?: any;
}

const props = defineProps<SplitLayoutFeatureSectionProps>();

const fields = computed(() => props.fields);
const rendering = computed(() => props.rendering);
const features = computed(() => fields.value?.features ?? []);
const imageSrc = computed(() => fields.value?.image?.value?.src || '');
const imageAlt = computed(
  () => fields.value?.image?.value?.alt || fields.value?.title?.value || ''
);
const FEATURE_TONES = ['green', 'light-green', 'gold', 'neutral'];

const getTone = (index: number) => FEATURE_TONES[index % FEATURE_TONES.length];
const hasFeatureImage = (feature: ScItem<SplitLayoutFeatureSectionFeatureFields>) =>
  Boolean(feature.fields?.icon?.value?.src);
const getFeatureImageSrc = (feature: ScItem<SplitLayoutFeatureSectionFeatureFields>) =>
  feature.fields?.icon?.value?.src || '';
const getFeatureImageAlt = (feature: ScItem<SplitLayoutFeatureSectionFeatureFields>) =>
  feature.fields?.icon?.value?.alt || feature.name || '';

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-icon-text-list'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-icon-text-list {
  padding: 40px 0;

  &__inner {
    width: 100%;
  }

  &__title {
    margin-bottom: 48px;
    text-align: center;
  }

  &__layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 28px;
    align-items: center;
  }

  &__media {
    overflow: hidden;
    border-radius: 12px;
    background: $color-light-mercury;
  }

  &__image {
    display: block;
    width: 100%;
    aspect-ratio: 1.36;
    object-fit: cover;
  }

  &__features {
    display: grid;
    gap: 10px;
  }

  &__feature {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 18px;
    align-items: center;
    padding: 10px 24px;
    border-radius: 11px;
    background: #f5f7f4;
  }

  &__feature--has-image {
    grid-template-columns: 50px minmax(0, 1fr);
  }

  &__feature--green {
    background: #f3f7f1;
  }

  &__feature--light-green {
    background: #eef6e8;
  }

  &__feature--gold {
    background: #fbf7ef;
  }

  &__feature--neutral {
    background: #f7f5f1;
  }

  &__feature-image {
    width: 50px;
    height: 50px;
    object-fit: contain;
  }

  &__feature-description {
    color: $black;
    font-size: 14px;
    line-height: 1.35;
    font-weight: 500;

    p {
      margin: 0;
    }
  }

  @include tablet {
    &__title {
      margin-bottom: 60px;
    }

    &__layout {
      grid-template-columns: minmax(0, 0.96fr) minmax(0, 1.04fr);
      gap: 72px;
    }

    &__features {
      gap: 15px;
    }

    &__feature {
      padding: 10px 24px;
    }
  }
}
</style>
